/**
 * Set who answers for one product under the CRA. Saves one
 * `ProduktVerantwortung` card through the generic Stammdaten CRUD — created on
 * the first save, updated afterwards.
 */

import { plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';

import EmptyState from '../../components/EmptyState';
import { Modal } from '../../components/Modal';
import Spinner from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import { createCmdbCard, deleteCmdbCard, errorMessage, updateCmdbCard } from '../../lib/api';
import type {
  CmdbLookupValue,
  ProduktVerantwortungValues,
  PsirtProductRow,
  PsirtTeam,
  PsirtThresholds,
} from '../../lib/types';

import PsirtChecklist from './PsirtChecklist';
import {
  KATEGORIE_FUNDSTELLE,
  KATEGORIE_ORDER,
  KATEGORIE_SHORT,
  PRODUCT_KEYS,
  addYears,
  dayValue,
  emailProblem,
  filled,
  fmtDayIso,
  fmtSpan,
  monthsBetween,
  productChecklist,
  stateName,
  toBody,
  todayIso,
} from './psirtModel';

export interface ProductFormModalProps {
  row: PsirtProductRow;
  teams: readonly PsirtTeam[];
  kategorien: readonly CmdbLookupValue[];
  kategorieFolgen: Readonly<Record<string, string>>;
  thresholds: PsirtThresholds;
  canWrite: boolean;
  onClose: () => void;
  onSaved: () => void;
  onCreateTeam: () => void;
}

type TextKey = Exclude<keyof ProduktVerantwortungValues, 'ProduktTypeID' | 'PsirtTeamID' | 'CraKategorie'>;

export function ProductFormModal({
  row,
  teams,
  kategorien,
  kategorieFolgen,
  thresholds,
  canWrite,
  onClose,
  onSaved,
  onCreateTeam,
}: ProductFormModalProps): ReactNode {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const toast = useToast();
  const [values, setValues] = useState<ProduktVerantwortungValues>(() => {
    const initial = row.values ?? {};
    return {
      ...initial,
      // A single team is the obvious default for a product without one.
      PsirtTeamID: initial.PsirtTeamID ?? (teams.length === 1 ? (teams[0]?.id ?? null) : null),
      SupportEnde: dayValue(initial.SupportEnde),
      GeprueftAm: dayValue(initial.GeprueftAm),
    };
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const readOnly = !canWrite || saving;
  const team = teams.find((entry) => entry.id === values.PsirtTeamID) ?? null;
  // Checklist labels are translated: rebuild them on a language switch.
  const checklist = useMemo(() => productChecklist(values, team?.values ?? null), [values, team, locale]);

  const set = (key: TextKey, value: string): void => {
    setValues((current) => ({ ...current, [key]: value }));
  };

  const problems: Partial<Record<TextKey, string | null>> = {
    OwnerEmail: emailProblem(values.OwnerEmail),
    VertretungEmail: emailProblem(values.VertretungEmail),
  };
  const hasProblems = Object.values(problems).some(Boolean);

  // --- Support period helpers ---------------------------------------------
  const today = todayIso();
  const minYears = thresholds.support_min_years;
  const basis = row.erstes_release ?? null;
  const supportEnde = filled(values.SupportEnde) ? String(values.SupportEnde) : null;
  const spanFromRelease = basis && supportEnde ? monthsBetween(basis, supportEnde) : null;
  const remaining = supportEnde ? monthsBetween(today, supportEnde) : null;
  const tooShort = spanFromRelease !== null && spanFromRelease < minYears * 12;
  const product = row.produkt;
  const deliverySummary = t`${plural(row.instanzen, {
    one: '# ausgelieferte Instanz',
    other: '# ausgelieferte Instanzen',
  })} bei ${plural(row.kunden, { one: '# Kunde', other: '# Kunden' })}`;
  const remainingSpan = fmtSpan(remaining ?? 0);
  const releaseSpan = spanFromRelease !== null ? fmtSpan(spanFromRelease) : '';
  const releaseDay = basis ? fmtDayIso(basis) : '';

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (hasProblems || readOnly) return;
    setSaving(true);
    setError(null);
    try {
      const body = toBody(values, PRODUCT_KEYS);
      if (row.verantwortung_id !== null) {
        await updateCmdbCard('ProduktVerantwortung', row.verantwortung_id, body);
      } else {
        await createCmdbCard('ProduktVerantwortung', { ...body, ProduktTypeID: row.produkt_id });
      }
      toast.success(t`Verantwortlichkeit für ${product} gespeichert.`);
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
      setSaving(false);
    }
  }

  async function remove(): Promise<void> {
    if (row.verantwortung_id === null) return;
    setSaving(true);
    setError(null);
    try {
      await deleteCmdbCard('ProduktVerantwortung', row.verantwortung_id);
      toast.success(t`Verantwortlichkeit für ${product} entfernt.`);
      onSaved();
    } catch (err) {
      setError(errorMessage(err));
      setSaving(false);
      setConfirmDelete(false);
    }
  }

  const text = (
    key: TextKey,
    label: string,
    options: { type?: string; placeholder?: string; hint?: ReactNode } = {},
  ): ReactNode => {
    const id = `psirt-product-${key}`;
    const problem = problems[key];
    return (
      <div className="field">
        <label htmlFor={id}>{label}</label>
        <input
          id={id}
          type={options.type ?? 'text'}
          value={String(values[key] ?? '')}
          placeholder={options.placeholder}
          disabled={readOnly}
          aria-invalid={Boolean(problem) || undefined}
          onChange={(event) => set(key, event.target.value)}
        />
        {options.hint ? <span className="hint">{options.hint}</span> : null}
        {problem ? <span className="err">{problem}</span> : null}
      </div>
    );
  };

  const teamGaps = team
    ? [
        !filled(team.values.MeldeEmail) ? t`Meldestelle` : null,
        !filled(team.values.CvdPolicyUrl) ? t`CVD-Policy` : null,
        !filled(team.values.CsirtKoordinator) || !filled(team.values.MelderEmail) ? t`Meldeweg` : null,
      ].filter((gap): gap is string => gap !== null)
    : [];
  const gapList = teamGaps.join(', ');
  const teamDeputy = team?.values.VertretungName ?? '';

  return (
    <Modal
      title={row.verantwortung_id === null ? t`Verantwortlichkeit festlegen` : t`Verantwortlichkeit bearbeiten`}
      subtitle={
        <>
          <strong>{row.produkt}</strong>
          {row.modellreihe ? ` · ${row.modellreihe}` : ''}
          {row.instanzen > 0 ? <> · {deliverySummary}</> : null}
        </>
      }
      size="wide"
      dismissable={!saving}
      onClose={onClose}
      footer={
        <>
          {row.verantwortung_id !== null && canWrite ? (
            confirmDelete ? (
              <button type="button" className="danger" onClick={() => void remove()} disabled={saving}>
                <Trans>Wirklich entfernen?</Trans>
              </button>
            ) : (
              <button type="button" className="ghost" onClick={() => setConfirmDelete(true)} disabled={saving}>
                <Trans>Zuordnung entfernen</Trans>
              </button>
            )
          ) : null}
          <span className="spacer" />
          <button type="button" onClick={onClose} disabled={saving}>
            {canWrite ? t`Abbrechen` : t`Schließen`}
          </button>
          {canWrite ? (
            <button type="submit" className="primary" form="psirt-product-form" disabled={saving}>
              {saving ? <Spinner variant="on-accent" label={t`Speichert…`} /> : t`Speichern`}
            </button>
          ) : null}
        </>
      }
    >
      <form id="psirt-product-form" className="psirt-form" onSubmit={submit} noValidate>
        <div className="psirt-form-main">
          {error ? (
            <div className="banner error" role="alert">
              {error}
            </div>
          ) : null}

          <section className="psirt-section">
            <header>
              <h3><Trans>Zuständigkeit</Trans></h3>
            </header>
            <div className="psirt-grid">
              <div className="field psirt-span">
                <label htmlFor="psirt-product-team"><Trans>PSIRT-Team</Trans></label>
                {teams.length === 0 ? (
                  <EmptyState
                    compact
                    title={t`Noch kein PSIRT-Team angelegt`}
                    action={
                      canWrite ? (
                        <button type="button" className="small" onClick={onCreateTeam}>
                          <Trans>Team anlegen</Trans>
                        </button>
                      ) : undefined
                    }
                  />
                ) : (
                  <select
                    id="psirt-product-team"
                    value={values.PsirtTeamID ?? ''}
                    disabled={readOnly}
                    onChange={(event) =>
                      setValues((current) => ({
                        ...current,
                        PsirtTeamID: event.target.value === '' ? null : Number(event.target.value),
                      }))
                    }
                  >
                    <option value="">{t`— kein Team —`}</option>
                    {teams.map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {entry.label}
                      </option>
                    ))}
                  </select>
                )}
                {team ? (
                  <div className="psirt-team-preview">
                    <span>
                      <small><Trans>Meldestelle</Trans></small>
                      {team.values.MeldeEmail || <em><Trans>fehlt</Trans></em>}
                    </span>
                    <span>
                      <small><Trans>Koordinierendes CSIRT</Trans></small>
                      {team.values.CsirtKoordinator
                        ? `${team.values.CsirtKoordinator}${team.values.Mitgliedstaat ? ` · ${stateName(team.values.Mitgliedstaat)}` : ''}`
                        : <em><Trans>fehlt</Trans></em>}
                    </span>
                    <span>
                      <small><Trans>Erreichbarkeit</Trans></small>
                      {team.values.Erreichbarkeit || <em><Trans>nicht angegeben</Trans></em>}
                    </span>
                    {teamGaps.length > 0 ? (
                      <p className="psirt-team-gap">
                        {t`Im Team fehlt noch: ${gapList}. Das betrifft jedes Produkt dieses Teams.`}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="psirt-grid psirt-grid-pairs">
              {text('OwnerName', t`Product Security Owner`, { placeholder: t`Vor- und Nachname` })}
              {text('OwnerEmail', t`E-Mail`, { type: 'email' })}
              {text('VertretungName', t`Stellvertretung`, {
                placeholder: teamDeputy ? t`${teamDeputy} (vom Team)` : t`Vor- und Nachname`,
              })}
              {text('VertretungEmail', t`E-Mail`, { type: 'email' })}
            </div>
          </section>

          <section className="psirt-section">
            <header>
              <h3><Trans>CRA-Produktkategorie</Trans></h3>
              <p><Trans>Entscheidet über das Konformitätsbewertungsverfahren (Art. 32, Anhang III/IV).</Trans></p>
            </header>
            <div className="psirt-kategorien" role="radiogroup" aria-label={t`CRA-Produktkategorie`}>
              {KATEGORIE_ORDER.map((code) => {
                const on = values.CraKategorie === code;
                return (
                  <label key={code} className={`psirt-kategorie${on ? ' on' : ''}`} data-kategorie={code}>
                    <input
                      type="radio"
                      name="psirt-kategorie"
                      value={code}
                      checked={on}
                      disabled={readOnly || kategorien.length === 0}
                      onChange={() => setValues((current) => ({ ...current, CraKategorie: code }))}
                    />
                    <strong>{KATEGORIE_SHORT[code]}</strong>
                    <span>{KATEGORIE_FUNDSTELLE[code]}</span>
                    <small>{kategorieFolgen[code]}</small>
                  </label>
                );
              })}
            </div>
            {kategorien.length === 0 ? (
              <p className="hint psirt-warn-text">
                <Trans>
                  Die Produktkategorien fehlen noch in CMDBuild. Ein Administrator muss das Datenmodell ergänzen.
                </Trans>
              </p>
            ) : null}
          </section>

          <section className="psirt-section">
            <header>
              <h3><Trans>Supportzeitraum</Trans></h3>
              <p>
                <Trans>
                  Mindestens {minYears} Jahre, außer die erwartete Nutzungsdauer ist kürzer. Das Enddatum muss
                  beim Kauf erkennbar sein (Art. 13 Abs. 8, Anhang II Nr. 7).
                </Trans>
              </p>
            </header>
            <div className="psirt-support">
              <div className="field">
                <label htmlFor="psirt-product-SupportEnde"><Trans>Support bis</Trans></label>
                <input
                  id="psirt-product-SupportEnde"
                  type="date"
                  value={values.SupportEnde ?? ''}
                  disabled={readOnly}
                  onChange={(event) => set('SupportEnde', event.target.value)}
                />
              </div>
              {canWrite ? (
                <div className="psirt-quick" aria-label={t`Schnellauswahl`}>
                  {basis ? (
                    <button
                      type="button"
                      className="small"
                      disabled={readOnly}
                      onClick={() => set('SupportEnde', addYears(basis, minYears))}
                    >
                      <Trans>{minYears} Jahre ab Erst-Release</Trans>
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="small"
                    disabled={readOnly}
                    onClick={() => set('SupportEnde', addYears(today, minYears))}
                  >
                    <Trans>{minYears} Jahre ab heute</Trans>
                  </button>
                  <button
                    type="button"
                    className="small"
                    disabled={readOnly}
                    onClick={() => set('SupportEnde', addYears(today, 10))}
                  >
                    <Trans>10 Jahre ab heute</Trans>
                  </button>
                </div>
              ) : null}
            </div>
            {supportEnde ? (
              <p className={`psirt-support-info${tooShort || (remaining !== null && remaining < 0) ? ' warn' : ''}`}>
                {remaining !== null && remaining < 0
                  ? t`Abgelaufen seit ${remainingSpan}, keine Sicherheitsupdates mehr zugesagt`
                  : t`Noch ${remainingSpan} ab heute`}
                {spanFromRelease !== null && basis
                  ? <> · {t`${releaseSpan} ab Erst-Release am ${releaseDay}`}</>
                  : null}
                {tooShort ? <>. {t`Unter ${minYears} Jahren: Begründung in der Bemerkung festhalten.`}</> : null}
              </p>
            ) : basis ? (
              <p className="psirt-support-info"><Trans>Erst-Release am {releaseDay}.</Trans></p>
            ) : null}
          </section>

          <section className="psirt-section">
            <header>
              <h3><Trans>Sicherheitsupdates</Trans></h3>
              <p><Trans>Kostenlos, getrennt von Funktionsupdates und möglichst automatisch (Anhang I Teil II Nr. 7–8).</Trans></p>
            </header>
            <div className="field">
              <label htmlFor="psirt-product-UpdateKanal"><Trans>Wie erreichen Updates die Kunden?</Trans></label>
              <textarea
                id="psirt-product-UpdateKanal"
                rows={3}
                value={values.UpdateKanal ?? ''}
                placeholder={t`z. B. Signierte Firmware-Pakete im Kundenportal, Benachrichtigung per E-Mail an den Betreiber, Einspielen durch den Service vor Ort.`}
                disabled={readOnly}
                onChange={(event) => set('UpdateKanal', event.target.value)}
              />
            </div>
          </section>

          <section className="psirt-section">
            <header>
              <h3><Trans>Überprüfung</Trans></h3>
              <p><Trans>Einmal im Jahr bestätigen, dass Personen und Zeiträume noch stimmen.</Trans></p>
            </header>
            <div className="psirt-support">
              <div className="field">
                <label htmlFor="psirt-product-GeprueftAm"><Trans>Zuletzt geprüft am</Trans></label>
                <input
                  id="psirt-product-GeprueftAm"
                  type="date"
                  value={values.GeprueftAm ?? ''}
                  max={today}
                  disabled={readOnly}
                  onChange={(event) => set('GeprueftAm', event.target.value)}
                />
              </div>
              {canWrite ? (
                <div className="psirt-quick">
                  <button
                    type="button"
                    className="small"
                    disabled={readOnly || values.GeprueftAm === today}
                    onClick={() => set('GeprueftAm', today)}
                  >
                    <Trans>Heute geprüft</Trans>
                  </button>
                </div>
              ) : null}
            </div>
            <div className="field">
              <label htmlFor="psirt-product-Bemerkung"><Trans>Bemerkung</Trans></label>
              <textarea
                id="psirt-product-Bemerkung"
                rows={3}
                value={values.Bemerkung ?? ''}
                placeholder={tooShort ? t`Begründung für den kürzeren Supportzeitraum …` : undefined}
                disabled={readOnly}
                aria-invalid={(tooShort && !filled(values.Bemerkung)) || undefined}
                onChange={(event) => set('Bemerkung', event.target.value)}
              />
            </div>
          </section>
        </div>

        <PsirtChecklist
          title={t`CRA-Anforderungen`}
          items={checklist}
          footnote={t`Die endgültige Bewertung erscheint nach dem Speichern in der Übersicht und im CRA-Check.`}
        />
      </form>
    </Modal>
  );
}

export default ProductFormModal;
